import React from 'react'
import { Control, Controller, FieldErrors } from 'react-hook-form'
import { match } from 'ts-pattern'

import { H6 } from '@frame/ui-primitives/h6'
import { Input } from '@frame/ui-primitives/input'

import { useDirectTask } from '@frame/lib/hooks/useDirectTask'
import { useCachedInfoStore } from '@frame/lib/stores/useCachedInfoStore'
import { cn } from '@frame/lib/utils/cn'
import { formatCurrency } from '@frame/lib/utils/format'
import { DataAttribute } from '@frame/types'

import { FormValues, PayUnit } from './types'
import { getDataCyForPayType, getLabelForPayType } from './utils'

const MAX_GROSS_BASE_PAY = 999999999 // Backend database field is NUMERIC(11,2)
const MAX_WEEKLY_HOURS = 168
const MAX_WEEKLY_HOURS_INPUT = 999 // Restrict to 3 digits

type Props = {
  selectedPayType: PayUnit
  disabled: boolean
  control: Control<FormValues>
  errors: FieldErrors<FormValues>
  clearField: (field: keyof FormValues) => void
  required?: boolean
}

export function PayTypeFields({ selectedPayType, disabled, control, errors, clearField, required = false }: Props) {
  const task = useDirectTask()
  const saveDetails = useCachedInfoStore(({ saveDetails }) => saveDetails)
  const taskId = task?.id ?? ''
  const payTypeLabelId = 'pay-type-label'

  function cacheEmploymentInfo({ field, value }: { field: keyof FormValues; value: string | boolean }) {
    saveDetails(taskId, { [field]: value })
  }

  return (
    <div>
      <H6 id={payTypeLabelId} className="font-medium text-moss-500" as="div">
        Select pay type
      </H6>{' '}
      <div className="mb-4 mt-2 flex justify-between" role="radiogroup" aria-labelledby={payTypeLabelId}>
        {Object.values(PayUnit).map((payUnit, idx) => {
          const isFirst = idx === 0
          const isLast = idx === Object.values(PayUnit).length - 1

          return (
            <Controller
              key={payUnit}
              name="pay_unit"
              control={control}
              render={({ field: { onChange } }) => {
                function handleChangePayUnit() {
                  clearField('hours_worked_per_week')
                  clearField('current_gross_base_pay')
                  onChange(payUnit)
                  cacheEmploymentInfo({ field: 'pay_unit', value: payUnit })
                }

                return (
                  <div
                    tabIndex={0}
                    key={payUnit}
                    onClick={handleChangePayUnit}
                    onKeyDown={(e) => e.key === 'Enter' && onChange(payUnit)}
                    className={cn(
                      'flex flex-1 cursor-pointer items-center justify-center border-[1px] border-moss-300 px-[20px] py-[14px] text-sm text-moss-700',
                      isFirst && 'rounded-l-sm border-r-none',
                      isLast && 'rounded-r-sm border-l-none',
                      selectedPayType === payUnit && 'border-cobranding bg-cobranding text-white transition-all',
                      disabled && 'pointer-events-none opacity-50'
                    )}
                    data-cy={getDataCyForPayType(payUnit)}
                    role="radio"
                    aria-checked={selectedPayType === payUnit}
                    aria-disabled={disabled}
                  >
                    {getLabelForPayType(payUnit)}
                  </div>
                )
              }}
            />
          )
        })}
      </div>
      <PayTypeInput
        payUnit={selectedPayType}
        disabled={disabled}
        control={control}
        errors={errors}
        cacheEmploymentInfo={cacheEmploymentInfo}
        required={required}
      />
    </div>
  )
}

type PayTypeInputProps = {
  payUnit: PayUnit
  disabled: boolean
  control: Control<FormValues>
  errors: FieldErrors<FormValues>
  cacheEmploymentInfo: ({ field, value }: { field: keyof FormValues; value: string | boolean }) => void
  required?: boolean
}

function PayTypeInput({ payUnit, disabled, control, errors, cacheEmploymentInfo, required }: PayTypeInputProps) {
  return match(payUnit)
    .with(PayUnit.ANNUALLY, PayUnit.OTHER, () => (
      <Controller
        name="current_gross_base_pay"
        control={control}
        rules={{
          pattern: {
            value: /\$ (\d|,)+$/,
            message: 'Must be a number',
          },
          validate: (value) => {
            if (Number(value?.replace(/[\\$\\,]/g, '')) > MAX_GROSS_BASE_PAY) {
              return `Maximum annual pay is ${formatCurrency(String(MAX_GROSS_BASE_PAY))}`
            }
          },
          required: { value: true, message: 'Required' },
        }}
        render={({ field: { value, name, onChange } }) => (
          <Input
            value={value}
            name={name}
            label="Annual Pay"
            placeholder="e.g. $50,000"
            data-cy={DataAttribute.POS_INCOME_INPUT}
            error={errors.current_gross_base_pay?.message}
            disabled={disabled}
            onChange={(e) => {
              const { value } = e.target
              const formattedValue = formatCurrency(value)
              onChange(formattedValue)
              cacheEmploymentInfo({ field: 'current_gross_base_pay', value: formattedValue })
            }}
            required={required}
          />
        )}
      />
    ))
    .with(PayUnit.HOURLY, () => (
      <div className="flex items-start space-x-3">
        <Controller
          name="current_gross_base_pay"
          control={control}
          rules={{
            pattern: {
              value: /\$ (\d{1,3}(,\d{3})*(\.\d{1,2})?)$/,
              message: 'Must be a number',
            },
            validate: (value) => {
              if (Number(value?.replace(/[\\$\\,]/g, '')) > MAX_GROSS_BASE_PAY) {
                return `Maximum hourly pay is ${formatCurrency(String(MAX_GROSS_BASE_PAY))}`
              }
            },
            required: { value: true, message: 'Required' },
          }}
          render={({ field: { value, name, onChange } }) => (
            <Input
              value={value}
              name={name}
              label="Hourly Pay"
              placeholder="e.g. $25.00"
              data-cy={DataAttribute.POS_INCOME_INPUT}
              error={errors.current_gross_base_pay?.message}
              disabled={disabled}
              className="flex-1"
              onChange={(e) => {
                const { value } = e.target
                const formattedValue = formatCurrency(value, true)
                onChange(formattedValue)
                cacheEmploymentInfo({ field: 'current_gross_base_pay', value: formattedValue })
              }}
              required={required}
            />
          )}
        />
        <Controller
          name="hours_worked_per_week"
          control={control}
          rules={{
            required: { value: true, message: 'Required' },
            max: { value: MAX_WEEKLY_HOURS, message: `Maximum hours is ${MAX_WEEKLY_HOURS}` },
          }}
          render={({ field: { value, name, onChange } }) => (
            <Input
              value={value}
              name={name}
              label="Weekly Hours"
              placeholder="e.g. 40"
              error={errors.hours_worked_per_week?.message}
              disabled={disabled}
              className="flex-1"
              data-cy={DataAttribute.WEEKLY_HOURS_INPUT}
              onChange={(e) => {
                let { value } = e.target
                value = value.replace(/\D/g, '')

                if (value && parseInt(value) > MAX_WEEKLY_HOURS_INPUT) {
                  value = value.slice(0, 3)
                }

                if (parseInt(value) <= 0) {
                  value = ''
                }

                onChange(value)
                cacheEmploymentInfo({ field: 'hours_worked_per_week', value })
              }}
              required={required}
            />
          )}
        />
      </div>
    ))
    .exhaustive()
}
